import React, { useState, useEffect, useMemo } from 'react';
import { leadsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Phone, Mail, FileSpreadsheet, FileDown, CalendarClock } from 'lucide-react';
import logger from '@/lib/logger';

const leadType = (lead) => {
  const s = (lead.service_interested || '').toLowerCase();
  if (s.startsWith('quote')) return 'quote';
  if (s.includes('checklist')) return 'checklist';
  return 'appointment';
};

const TYPE_META = {
  quote: { label: 'Quote Request', badge: 'bg-emerald-100 text-emerald-700', Icon: FileSpreadsheet },
  checklist: { label: 'Checklist', badge: 'bg-sky-100 text-sky-700', Icon: FileDown },
  appointment: { label: 'Appointment', badge: 'bg-amber-100 text-amber-700', Icon: CalendarClock },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'quote', label: 'Quote Requests' },
  { key: 'checklist', label: 'Checklist' },
  { key: 'appointment', label: 'Appointments' },
];

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await leadsAPI.list();
        setLeads(res.data);
      } catch (err) { logger.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const counts = useMemo(() => {
    const c = { all: leads.length, quote: 0, checklist: 0, appointment: 0 };
    leads.forEach(l => { c[leadType(l)] += 1; });
    return c;
  }, [leads]);

  const visible = useMemo(
    () => (filter === 'all' ? leads : leads.filter(l => leadType(l) === filter)),
    [leads, filter]
  );

  if (loading) return <div className="h-64 bg-muted rounded-xl animate-pulse" />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold">Leads & Appointments</h2>
        <p className="text-sm text-muted-foreground mt-1">{leads.length} total · {counts.quote} quote request{counts.quote !== 1 ? 's' : ''}</p>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="leads-filter-tabs">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`leads-filter-${f.key}`}
            className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors ${
              filter === f.key
                ? 'bg-[#042F2E] text-white border-[#042F2E]'
                : 'bg-white text-foreground/70 border-border hover:bg-muted'
            }`}
          >
            {f.label} <span className={`ml-1.5 ${filter === f.key ? 'text-white/70' : 'text-muted-foreground'}`}>{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-1">No {filter === 'all' ? 'leads' : FILTERS.find(f => f.key === filter).label.toLowerCase()} yet</h3>
            <p className="text-sm text-muted-foreground">Requests from the website will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead className="hidden lg:table-cell">Interest</TableHead>
                <TableHead className="hidden md:table-cell">Details</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map(lead => {
                const t = leadType(lead);
                const meta = TYPE_META[t];
                return (
                  <TableRow key={lead.id} data-testid={`lead-row-${t}`}>
                    <TableCell>
                      <Badge variant="secondary" className={`text-xs gap-1 ${meta.badge}`}>
                        <meta.Icon className="h-3 w-3" /> {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{lead.name}</p>
                        {lead.company_name && <p className="text-xs text-muted-foreground">{lead.company_name}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" /> {lead.email}</p>
                        {lead.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" /> {lead.phone}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{lead.service_interested || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-muted-foreground max-w-[280px] whitespace-pre-line" title={lead.notes || ''}>
                      {lead.notes ? (lead.notes.length > 180 ? lead.notes.slice(0, 180) + '…' : lead.notes) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700">{lead.status}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default LeadsPage;
